import React, { useState, useMemo, useEffect, useRef } from 'react';
import Card from '../../components/Card';
import {
  CardV2,
  CARD_DISPLAY_SIZES,
  calculateCardPositions,
  calculateResponsiveHandSize,
  DESKTOP_ARC,
  MOBILE_ARC,
} from '../../components/cardV2/index';
import FieldMonster from '../../components/FieldMonster';
import SPTokens from '../../components/SPTokens';
import DeepSeaIndicator from './DeepSeaIndicator';
import { getStageShortName } from '../../engine/phaseCardEffects';
import { getDirectAttackStatus } from '../../engine/keywordAbilities';
import { MAX_SP } from '../../utils/constants';
import { useBattle } from '../context';

import type { PlayerNumber } from '../../types/player';
import type { HandCard } from '../../types/card';

/** PlayerType: プレイヤーの種類 */
type PlayerType = 'human' | 'ai';

/** コスト修正情報 */
interface CostModifierInfo {
  modifiedCost?: number;
  costModifierSource?: string | null;
}

/** スタイルオブジェクト */
interface StyleObject {
  [key: string]: React.CSSProperties | StyleObject;
}

/** PlayerArea Props */
interface PlayerAreaProps {
  player: PlayerNumber;
  bonusSP: number;
  playerType: PlayerType;
  opponentPlayerType: PlayerType;
  onHandCardClick: (card: HandCard, index?: number) => void;
  onFieldSlotClick: (slotIndex: number, playerNum: PlayerNumber) => void;
  onFieldCardZoneClick: () => void;
  onPhaseCardZoneClick: () => void;
  onGraveyardClick: () => void;
  getModifiedCostInfo: (card: HandCard, player: PlayerNumber) => CostModifierInfo;
  continuousEffectEngine: any;
  statusEffectEngine: any;
  styles: StyleObject;
  mobileStyles: StyleObject;
  mergeStyles: (base: React.CSSProperties, mobile: React.CSSProperties, isMobile: boolean) => React.CSSProperties;
  initialLifePoints: number;
}

/**
 * PlayerArea
 *
 * P1/P2 共通のプレイヤーエリアコンポーネント
 * - 情報パネル (LP, SP, デッキ, 墓地)
 * - 手札エリア
 * - モンスターゾーン
 * - 刹那詠唱UI
 * - フィールド/フェイズカードゾーン
 *
 * Phase 5-3: Context から状態を取得
 */
const PlayerArea: React.FC<PlayerAreaProps> = ({
  // プレイヤー識別
  player, // 1 or 2

  // ========================================
  // Context に存在しない props（引き続き必要）
  // ========================================

  // プレイヤーデータ（Context 未対応）
  bonusSP,

  // プレイヤータイプ（手札表示判定用）
  playerType,
  opponentPlayerType,

  // コールバック
  onHandCardClick,
  onFieldSlotClick,
  onFieldCardZoneClick,
  onPhaseCardZoneClick,
  onGraveyardClick,

  // コスト計算
  getModifiedCostInfo,

  // 常時効果エンジン
  continuousEffectEngine,
  statusEffectEngine,

  // スタイル
  styles,
  mobileStyles,
  mergeStyles,

  // 定数
  initialLifePoints,
}) => {
  // ========================================
  // Context から状態を取得
  // ========================================
  const {
    // ゲーム状態
    currentPlayer,
    phase,
    isMobile,

    // プレイヤーデータ（P1/P2 両方）
    p1Life, p2Life,
    p1Deck, p2Deck,
    p1Hand, p2Hand,
    p1Field, p2Field,
    p1Graveyard, p2Graveyard,
    p1ActiveSP, p2ActiveSP,
    p1RestedSP, p2RestedSP,
    p1FieldCard, p2FieldCard,
    p1PhaseCard, p2PhaseCard,

    // 選択状態（selection から）
    selectedHandCard,
    selectedFieldMonster,
    attackingMonster,
    pendingSelectedCard,
    pendingMonsterTarget,
    pendingSelectedMonsterIndex,
    selectedFieldCardInfo,
    setHoveredCard,
    setMobileCardDetail,

    // 深海領域
    deepSea,
  } = useBattle();

  // ========================================
  // プレイヤー番号に応じて状態を選択
  // ========================================
  const life = player === 1 ? p1Life : p2Life;
  const deck = player === 1 ? p1Deck : p2Deck;
  const hand = player === 1 ? p1Hand : p2Hand;
  const field = player === 1 ? p1Field : p2Field;
  const graveyard = player === 1 ? p1Graveyard : p2Graveyard;
  const activeSP = player === 1 ? p1ActiveSP : p2ActiveSP;
  const restedSP = player === 1 ? p1RestedSP : p2RestedSP;
  const fieldCard = player === 1 ? p1FieldCard : p2FieldCard;
  const phaseCard = player === 1 ? p1PhaseCard : p2PhaseCard;
  const opponentField = player === 1 ? p2Field : p1Field;
  const playerDeepSeaZone = deepSea.getPlayerDeepSeaZone(player);
  // 現在のプレイヤーのフィールド（飛行システム用）
  const currentPlayerField = currentPlayer === 1 ? p1Field : p2Field;
  // プレイヤー別の色定義
  const colors = player === 1
    ? {
        primary: '#4da6ff',
        secondary: '#66d9ff',
        bg: 'rgba(77,166,255,0.1)',
        graveyard: '#6b9eff',
        gradient: 'linear-gradient(90deg, #4da6ff, #66d9ff)',
      }
    : {
        primary: '#ff6b6b',
        secondary: '#ff8533',
        bg: 'rgba(255,107,107,0.1)',
        graveyard: '#9c6bff',
        gradient: 'linear-gradient(90deg, #ff6b6b, #ff8533)',
      };

  // 現在プレイヤーかどうか
  const isCurrentPlayer = currentPlayer === player;

  // 手札表示判定
  // P1: AI && P2が人間 → 裏向き、それ以外 → 表向き（自分のターンかどうかに関係なく）
  // P2: 自分のターン && 人間 → 表向き、それ以外 → 裏向き
  const shouldShowHand = player === 1
    ? !(playerType === 'ai' && opponentPlayerType === 'human')
    : (isCurrentPlayer && playerType === 'human');

  // ========================================
  // 手札扇形表示用のstate
  // ========================================
  const [hoveredHandCardIndex, setHoveredHandCardIndex] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(600);
  const fieldAreaRef = useRef<HTMLDivElement>(null);

  // コンテナ幅をResizeObserverで監視
  useEffect(() => {
    const element = fieldAreaRef.current;
    if (!element) return;

    const updateWidth = () => {
      const width = element.offsetWidth;
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    // 初回計測
    updateWidth();

    // ResizeObserver で監視
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  // 手札の基準サイズ（自分の手札は大きく、相手の手札は小さく）
  const baseHandWidth = isMobile
    ? (shouldShowHand ? CARD_DISPLAY_SIZES.handMobileSelf.width : CARD_DISPLAY_SIZES.handMobileOpponent.width)
    : (shouldShowHand ? CARD_DISPLAY_SIZES.handDesktopSelf.width : CARD_DISPLAY_SIZES.handDesktopOpponent.width);

  // 手札エリアの利用可能幅（実測値を使用）
  const handAreaWidth = useMemo(() => {
    // コンテナ幅からマージンを引く（左端カードのコストが見えるよう調整）
    const margin = isMobile ? 32 : 80;
    const available = containerWidth - margin;
    // 最小幅を確保
    return Math.max(isMobile ? 280 : 300, available);
  }, [isMobile, containerWidth]);

  // レスポンシブ手札サイズ計算（ハイブリッド方式）
  const responsiveHandSize = useMemo(() => {
    const minCardWidth = isMobile ? 60 : 80; // 最小カードサイズ
    return calculateResponsiveHandSize(
      handAreaWidth,
      hand.length,
      baseHandWidth,
      minCardWidth
    );
  }, [handAreaWidth, hand.length, baseHandWidth, isMobile]);

  const handDisplayWidth = responsiveHandSize.cardWidth;
  const handDisplayHeight = responsiveHandSize.cardHeight;

  // 手札カードの弧状配置を計算（カスタム重なりを使用）
  const handCardPositions = useMemo(() => {
    const arcConfig = isMobile ? MOBILE_ARC : DESKTOP_ARC;
    // calculateCardPositions の重なり計算を上書きするため、カスタム設定を作成
    const customArcConfig = {
      ...arcConfig,
      // レスポンシブ計算で得た重なりを反映（minOverlap/maxOverlap を同じ値に）
      minOverlap: responsiveHandSize.overlap,
      maxOverlap: responsiveHandSize.overlap,
    };
    return calculateCardPositions(
      hand.length,
      handAreaWidth,
      handDisplayWidth,
      handDisplayHeight,
      customArcConfig
    );
  }, [hand.length, handAreaWidth, handDisplayWidth, handDisplayHeight, isMobile, responsiveHandSize.overlap]);

  // 情報パネルをレンダリング
  const renderInfoPanel = () => (
    <div style={mergeStyles(styles.infoPanel, mobileStyles.infoPanel, isMobile)}>
      {isMobile ? (
        /* モバイル: 横並びコンパクト表示 */
        <>
          <span style={{ fontWeight: 'bold', color: colors.primary }}>P{player}</span>
          <span style={{ color: colors.secondary }}>LP:{life}</span>
          <span>SP:{activeSP}/{activeSP + restedSP}</span>
          <span style={{ color: '#888' }}>📦{deck.length}</span>
          <span
            onClick={() => graveyard.length > 0 && onGraveyardClick()}
            style={{
              cursor: graveyard.length > 0 ? 'pointer' : 'default',
              color: graveyard.length > 0 ? colors.graveyard : '#888',
            }}
          >
            🪦{graveyard.length}
          </span>
          <DeepSeaIndicator
            deepSeaZone={playerDeepSeaZone}
            onClick={() => deepSea.setShowDeepSeaModal(player)}
            isOwn={isCurrentPlayer}
            isMobile={true}
          />
        </>
      ) : (
        /* デスクトップ: 従来表示 */
        <>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: colors.primary }}>
            プレイヤー{player}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>LP: {life}</div>
            <div style={styles.lifeBar}>
              <div style={{
                ...styles.lifeBarFill,
                width: `${(life / initialLifePoints) * 100}%`,
                background: colors.gradient,
              }} />
            </div>
          </div>
          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
            SP: {activeSP}/{activeSP + restedSP}{bonusSP > 0 ? ` +☆${bonusSP}` : ''}
          </div>
          <SPTokens active={activeSP} rested={restedSP} max={MAX_SP} bonus={bonusSP} />
          <div style={{ fontSize: '11px', marginTop: '8px', color: '#888', display: 'flex', gap: '8px' }}>
            <span>デッキ: {deck.length}</span>
            <span
              onClick={() => graveyard.length > 0 && onGraveyardClick()}
              style={{
                cursor: graveyard.length > 0 ? 'pointer' : 'default',
                color: graveyard.length > 0 ? colors.graveyard : '#888',
                textDecoration: graveyard.length > 0 ? 'underline' : 'none',
              }}
              title={graveyard.length > 0 ? 'クリックして墓地を閲覧' : '墓地にカードがありません'}
            >
              墓地: {graveyard.length}
            </span>
          </div>
          <div style={{ marginTop: '8px' }}>
            <DeepSeaIndicator
              deepSeaZone={playerDeepSeaZone}
              onClick={() => deepSea.setShowDeepSeaModal(player)}
              isOwn={isCurrentPlayer}
              isMobile={false}
            />
          </div>
        </>
      )}
    </div>
  );

  // 手札エリアをレンダリング（扇形表示）
  const renderHandArea = () => {
    // 相手（裏面表示）の場合は高さを削減して下半分だけ表示
    const isOpponentHidden = player === 2 && !shouldShowHand;
    const baseContainerHeight = handDisplayHeight + 40;
    // 相手の場合: カード高さの55%程度に削減（下半分だけ表示）
    const containerHeight = isOpponentHidden
      ? Math.round(handDisplayHeight * 0.55)
      : baseContainerHeight;
    // 下半分を表示するためのオフセット（カードの上部をコンテナ外に出す）
    const hiddenOffset = isOpponentHidden ? -(handDisplayHeight - containerHeight) : 0;

    return (
      <div
        style={{
          position: 'relative',
          width: handAreaWidth,
          height: containerHeight,
          margin: '0 auto',
          // 相手の裏面手札はoverflow:hiddenで上部をカット、自分は弧状配置のためvisible
          overflow: isOpponentHidden ? 'hidden' : 'visible',
        }}
      >
        {hand.map((card, index) => {
          const costInfo = isCurrentPlayer ? getModifiedCostInfo(card, player) : {};
          const position = handCardPositions[index];
          const isSelected = selectedHandCard?.uniqueId === card.uniqueId || pendingSelectedCard?.uniqueId === card.uniqueId;
          const isHovered = hoveredHandCardIndex === index;

          if (!position) return null;

          // 相手の裏面手札は弧を反転（下に凸）
          const adjustedY = isOpponentHidden ? -position.y : position.y;
          const adjustedRotation = isOpponentHidden ? -position.rotation : position.rotation;

          return shouldShowHand ? (
            <div
              key={card.uniqueId}
              style={{
                position: 'absolute',
                left: position.x,
                top: position.y + 20, // 上部余白
              }}
            >
              <CardV2
                card={card}
                displayMode="hand"
                displayWidth={handDisplayWidth}
                onClick={() => onHandCardClick(card)}
                selected={isSelected}
                isHovered={isHovered}
                isMobile={isMobile}
                disabled={!isCurrentPlayer || phase !== 2}
                modifiedCost={costInfo.modifiedCost}
                costModifierSource={costInfo.costModifierSource ?? undefined}
                rotation={position.rotation}
                offsetY={0}
                zIndex={position.zIndex}
                onMouseEnter={() => {
                  setHoveredHandCardIndex(index);
                  setHoveredCard({ ...card, owner: player });
                }}
                onMouseLeave={() => {
                  setHoveredHandCardIndex(null);
                  setHoveredCard(null);
                }}
                onLongPress={(c) => setMobileCardDetail({ card: c, position: { x: 0, y: 0 } })}
              />
            </div>
          ) : (
            <div
              key={card.uniqueId}
              style={{
                position: 'absolute',
                left: position.x,
                // 下半分だけ表示: カードの上部をコンテナ外に出す
                top: hiddenOffset,
              }}
            >
              <CardV2
                card={card}
                displayMode="hand"
                displayWidth={handDisplayWidth}
                faceDown
                isMobile={isMobile}
                rotation={0}
                zIndex={position.zIndex}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // モンスターゾーンをレンダリング
  const renderMonsterZone = () => (
    <div style={mergeStyles(styles.monsterZone, mobileStyles.monsterZone, isMobile)}>
      {field.map((monster, i) => {
        // 常時効果による修正値を計算
        const effectContext = {
          currentPlayer,
          effectOwner: player,
          p1Field: player === 1 ? field : opponentField,
          p2Field: player === 2 ? field : opponentField,
          p1Life: player === 1 ? life : 0, // 相手ライフは使わないので0
          p2Life: player === 2 ? life : 0,
        };
        const continuousAtkMod = monster ? continuousEffectEngine.calculateAttackModifier(monster, effectContext) : 0;
        const statusAtkMod = monster ? statusEffectEngine.getAttackModifier(monster) : 0;
        const atkMod = continuousAtkMod + statusAtkMod;
        const hpMod = monster ? continuousEffectEngine.calculateHPModifier(monster, effectContext) : 0;

        // ターゲット選択判定
        const opponentPlayer = player === 1 ? 2 : 1;
        const isTargetSelectable = !!(pendingMonsterTarget && (
          (pendingMonsterTarget.targetPlayer === 'opponent' && (pendingMonsterTarget.selectionOwner ?? currentPlayer) === opponentPlayer) ||
          (pendingMonsterTarget.targetPlayer === 'self' && (pendingMonsterTarget.selectionOwner ?? currentPlayer) === player)
        ) && monster);
        const isTargetSelected = !!(pendingMonsterTarget && (
          (pendingMonsterTarget.targetPlayer === 'opponent' && (pendingMonsterTarget.selectionOwner ?? currentPlayer) === opponentPlayer) ||
          (pendingMonsterTarget.targetPlayer === 'self' && (pendingMonsterTarget.selectionOwner ?? currentPlayer) === player)
        ) && pendingSelectedMonsterIndex === i);

        // ダイレクトアタック可能判定: 相手側・バトルフェイズ・攻撃者選択中
        // 条件: フィールドが空 OR 飛行モンスターが地上モンスターのみをバイパス
        const attacker = attackingMonster !== null ? currentPlayerField[attackingMonster] : null;
        const flightDirectStatus = attacker ? getDirectAttackStatus(attacker, field) : { canDirect: false };
        const isDirectAttackTarget = !!(
          !isCurrentPlayer &&
          phase === 3 &&
          attackingMonster !== null &&
          !monster &&
          (!field.some(m => m !== null) || flightDirectStatus.canDirect)
        );

        return (
          <FieldMonster
            key={i}
            monster={monster as any}
            onClick={() => onFieldSlotClick(i, player)}
            selected={selectedFieldMonster === i && currentPlayer === player}
            canAttack={isCurrentPlayer && phase === 3 && monster?.canAttack}
            isTarget={!isCurrentPlayer && phase === 3 && attackingMonster !== null}
            isValidTarget={!!(isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'monster' && !monster)}
            isDirectAttackTarget={isDirectAttackTarget}
            isTargetSelectable={isTargetSelectable}
            isTargetSelected={isTargetSelected}
            onMouseEnter={monster ? () => setHoveredCard({ ...monster, owner: player }) : undefined}
            onMouseLeave={monster ? () => setHoveredCard(null) : undefined}
            atkModifier={atkMod}
            hpModifier={hpMod}
            isMobile={isMobile}
            onLongPress={(m) => setMobileCardDetail({ card: m as any, position: { x: 0, y: 0 } })}
          />
        );
      })}
    </div>
  );

  // フィールド/フェイズカードゾーンをレンダリング
  const renderFieldPhaseZone = () => (
    <div style={isMobile ? mergeStyles(styles.infoPanel, mobileStyles.fieldPhaseZone, isMobile) : styles.infoPanel}>
      <div style={isMobile ? mobileStyles.fieldPhaseZoneContainer : styles.cardZoneContainer}>
        {/* フィールドカード */}
        <div style={isMobile ? mobileStyles.fieldPhaseZoneItem : styles.cardZoneItem}>
          <div style={isMobile ? mobileStyles.fieldPhaseZoneLabel : { fontSize: '12px', marginBottom: '8px' }}>フィールド</div>
          {fieldCard ? (
            <div
              style={{
                cursor: isCurrentPlayer && phase === 2 ? 'pointer' : 'default',
                border: selectedFieldCardInfo?.card === fieldCard ? `2px solid ${colors.primary}` : 'none',
                borderRadius: '4px',
                padding: '2px',
              }}
              onClick={isCurrentPlayer ? onFieldCardZoneClick : undefined}
              onMouseEnter={() => setHoveredCard({ ...fieldCard, owner: player, _fieldCardInfo: { type: 'field', player } } as any)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card card={fieldCard} small isMobile={isMobile} onLongPress={(c) => setMobileCardDetail({ card: c, position: { x: 0, y: 0 } })} />
            </div>
          ) : (
            <div
              style={{
                ...styles.cardSlot,
                ...(isMobile ? mobileStyles.fieldPhaseZoneSlot : { width: '80px', height: '100px' }),
                cursor: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'field' ? 'pointer' : 'default',
                border: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'field' ? `2px solid ${colors.primary}` : '1px dashed #444',
                background: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'field' ? `${colors.bg}` : 'rgba(30,30,40,0.5)',
              }}
              onClick={isCurrentPlayer ? onFieldCardZoneClick : undefined}
            >
              なし
            </div>
          )}
        </div>

        {/* フェイズカード */}
        <div style={isMobile ? mobileStyles.fieldPhaseZoneItem : styles.cardZoneItem}>
          <div style={isMobile ? mobileStyles.fieldPhaseZoneLabel : { fontSize: '12px', marginBottom: '8px' }}>フェイズ</div>
          {phaseCard ? (
            <div
              style={{
                cursor: isCurrentPlayer && phase === 2 ? 'pointer' : 'default',
                border: isCurrentPlayer && phase === 2 && selectedHandCard && (selectedHandCard.type === 'monster' || selectedHandCard.type === 'magic' || selectedHandCard.type === 'field' || selectedHandCard.type === 'phasecard')
                  ? `2px solid ${colors.primary}`
                  : selectedFieldCardInfo?.card === phaseCard ? `2px solid ${colors.primary}` : 'none',
                borderRadius: '4px',
                padding: '2px',
              }}
              onClick={isCurrentPlayer ? onPhaseCardZoneClick : undefined}
              onMouseEnter={() => setHoveredCard({ ...phaseCard, owner: player, _fieldCardInfo: { type: 'phasecard', player } } as any)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card card={phaseCard} small isMobile={isMobile} onLongPress={(c) => setMobileCardDetail({ card: c, position: { x: 0, y: 0 } })} />
              {!isMobile && (
                <div style={{ fontSize: '10px', color: '#ffd700', textAlign: 'center', marginTop: '4px' }}>
                  ⚡ {getStageShortName(phaseCard.stage || 0)}段階 ({phaseCard.charges?.length || 0}/3)
                </div>
              )}
              {isCurrentPlayer && phase === 2 && selectedHandCard &&
                (selectedHandCard.type === 'monster' || selectedHandCard.type === 'magic' || selectedHandCard.type === 'field' || selectedHandCard.type === 'phasecard') &&
                (selectedHandCard.attribute === phaseCard.attribute || selectedHandCard.attribute === 'なし' || phaseCard.attribute === 'なし') &&
                (phaseCard.stage || 0) < 3 && !isMobile && (
                <div style={{ fontSize: '9px', color: colors.primary, textAlign: 'center', marginTop: '2px' }}>
                  クリックでチャージ
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                ...styles.cardSlot,
                ...(isMobile ? mobileStyles.fieldPhaseZoneSlot : { width: '80px', height: '100px' }),
                cursor: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'phasecard' ? 'pointer' : 'default',
                border: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'phasecard' ? `2px solid ${colors.primary}` : '1px dashed #444',
                background: isCurrentPlayer && phase === 2 && selectedHandCard && selectedHandCard.type === 'phasecard' ? `${colors.bg}` : 'rgba(30,30,40,0.5)',
              }}
              onClick={isCurrentPlayer ? onPhaseCardZoneClick : undefined}
            >
              {isMobile ? '—' : 'フェイズ'}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // フィールドエリアをレンダリング（P1/P2でレイアウト順が異なる）
  const renderFieldArea = () => (
    <div ref={fieldAreaRef} style={styles.fieldArea}>
      {player === 2 ? (
        // P2: hand → monster
        <>
          {renderHandArea()}
          {renderMonsterZone()}
        </>
      ) : (
        // P1: monster → hand
        <>
          {renderMonsterZone()}
          {renderHandArea()}
        </>
      )}
    </div>
  );

  return (
    <div style={{
      ...mergeStyles(styles.playerArea, mobileStyles.playerArea, isMobile),
      background: isCurrentPlayer ? colors.bg : 'transparent'
    }}>
      {renderInfoPanel()}
      {renderFieldArea()}
      {renderFieldPhaseZone()}
    </div>
  );
};

export default PlayerArea;
